<template>
  <ValidationObserver v-slot="{ handleSubmit, valid }">
    <Loading v-show="loading" />
    <form v-show="!loading" class="p-box" @submit.prevent="handleSubmit(handleUpdateAttributes)">
      <div class="p-box__body">
        <div class="container">
          <div class="geoContainer">
            <div id="map" />
            <div class="geoContainer__form">
              <ValidationProvider v-slot="{ errors, value, handleChange }"
                                  v-model="form.latitude"
                                  :name="$t('pages.CompaniesLocationStoreShufooEdit.latitude')"
                                  rules="required|latitude"
              >
                <label class="c-formLabel">
                  <p>{{ $t('pages.CompaniesLocationStoreShufooEdit.latitude') }}{{ $t('labels.requiredLabel') }}</p>
                </label>
                <input
                  :value="value"
                  type="text"
                  class="c-textField"
                  :placeholder="$t('labels.placeholder.basic')"
                  @input="handleChange"
                >
                <span v-for="error in errors" :key="error" class="c-error-msg">
                  {{ error }}
                </span>
              </ValidationProvider>
              <ValidationProvider v-slot="{ errors, value, handleChange }"
                                  v-model="form.longitude"
                                  :name="$t('pages.CompaniesLocationStoreShufooEdit.longitude')"
                                  rules="required|longitude"
              >
                <label class="c-formLabel">
                  <p>{{ $t('pages.CompaniesLocationStoreShufooEdit.longitude') }}{{ $t('labels.requiredLabel') }}</p>
                </label>
                <input
                  :value="value"
                  type="text"
                  class="c-textField"
                  :placeholder="$t('labels.placeholder.basic')"
                  @input="handleChange"
                >
                <span v-for="error in errors" :key="error" class="c-error-msg">
                  {{ error }}
                </span>
              </ValidationProvider>
            </div>
          </div>
        </div>
        <div class="container">
          <ValidationProvider
            v-slot="{ value, handleChange }"
            v-model="form.publishStatus"
            :name="$t('pages.CompaniesLocationStoreShufooEdit.publishStatus.label')"
            rules="required"
          >
            <label class="c-formLabel">
              <p>{{ $t('pages.CompaniesLocationStoreShufooEdit.publishStatus.label') }}{{ $t('labels.requiredLabel') }}</p>
            </label>
            <div class="select">
              <Select
                :model-value="value"
                :options="publishStatusSelects"
                @update:modelValue="handleChange"
              />
            </div>
          </ValidationProvider>
        </div>
        <div class="container">
          <label class="c-formLabel">
            <p>{{ $t('pages.CompaniesLocationStoreShufooEdit.businessHoursCaption') }}</p>
          </label>
          <input
            v-model="form.businessHoursCaption"
            type="text"
            class="c-textField"
            :placeholder="$t('labels.placeholder.basic')"
          >
          <span class="example">{{ $t('pages.CompaniesLocationStoreShufooEdit.businessHoursCaptionExample') }}</span>
        </div>
        <div class="container">
          <label class="c-formLabel">
            <p>{{ $t('pages.CompaniesLocationStoreShufooEdit.code') }}</p>
          </label>
          <input
            v-model="form.code"
            type="text"
            class="c-textField"
            :placeholder="$t('labels.placeholder.basic')"
          >
        </div>
        <div class="container">
          <label class="c-formLabel">
            <p>{{ $t('pages.CompaniesLocationStoreShufooEdit.referralUrl') }}</p>
          </label>
          <input
            v-model="form.referralUrl"
            type="url"
            class="c-textField"
            :placeholder="$t('labels.placeholder.basic')"
          >
        </div>
      </div>
      <div class="p-box__footer">
        <div class="c-button c-button--right">
          <router-link
            :to="{ name: 'CompaniesLocationStore', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
            class="c-button__item c-button__item--outline-cancel"
          >
            {{ $t('labels.cancel') }}
          </router-link>
          <button
            class="c-button__item c-button__item--primary"
            :class="{'c-button__item--disabled': !valid}" :disabled="!valid"
          >
            {{ $t('labels.save') }}
          </button>
        </div>
      </div>
    </form>
  </ValidationObserver>
</template>
<script>
import { mapState, mapActions } from 'vuex'
import Loading from '../components/Loading.vue'
import Select from '../components/Select'
import { mapionSdkOrigin } from '../modules/mapionSdkOrigin'
import { boundaryOfJapanMap, isValidLatitude, isValidLongitude } from '../plugins/vee-validate'

export default {
  name: 'CompaniesLocationStoreShufooEdit',
  components: {
    Loading,
    Select,
  },
  emits: ['update-title'],
  data() {
    return {
      form: {
        businessHoursCaption: '',
        code: '',
        referralUrl: '',
        latitude: null,
        longitude: null,
        publishStatus: null
      },
      map: null,
      marker: null,
    }
  },
  computed: {
    ...mapState({
      shufooAttributes: state => state.companiesStoreOcShufooAttributes.data,
      shufooAttributesLoading: state => state.companiesStoreOcShufooAttributes.loading,
      store: state => state.companiesLocationStore.data,
      storeLoading: state => state.companiesLocationStore.loading,
    }),
    companyUuid() {
      return this.$route.params.company_uuid
    },
    storeUuid() {
      return this.$route.params.store_uuid
    },
    loading() {
      return this.shufooAttributesLoading || this.shufooAttributesLoading
    },
    geo() {
      return [this.form.latitude, this.form.longitude]
    },
    publishStatusSelects() {
      const open_label = this.$t('pages.CompaniesLocationStoreShufooEdit.publishStatus.kind.open')
      const close_label = this.$t('pages.CompaniesLocationStoreShufooEdit.publishStatus.kind.close')
      const delete_label = this.$t('pages.CompaniesLocationStoreShufooEdit.publishStatus.kind.delete')
      const result = {}
      result[open_label] = 'open'
      result[close_label] = 'close'
      result[delete_label] = 'delete'
      return result
    }
  },
  watch: {
    geo: {
      handler(value) {
        const [latitude, longitude] = value
        if(!this.map) this.setMap()
        if(latitude && longitude) {
          const point = L.latLng(latitude, longitude)
          if(!this.marker) this.setMarker()

          if(isValidLatitude(point.lat) && isValidLongitude(point.lng)) {
            this.marker.setLatLng(point)
            this.map.panTo(point)
          }
        }
        this.map.invalidateSize()
      }
    },
  },
  beforeCreate() {
    const scriptEl = document.createElement('script')
    scriptEl.setAttribute('src', `${ mapionSdkOrigin }/maps/api/js?key=mov_lone_map&v=1.3&dtm=jgd`)
    const linkLeaflet = document.createElement('link')
    linkLeaflet.setAttribute('href', `${ mapionSdkOrigin }/maps/api/v1.3/leaflet.css`)
    linkLeaflet.setAttribute('rel', 'stylesheet')
    const linkMapion = document.createElement('link')
    linkMapion.setAttribute('href', `${ mapionSdkOrigin }/maps/api/v1.3/mapion-plugin.css`)
    linkMapion.setAttribute('rel', 'stylesheet')
    document.head.appendChild(scriptEl)
    document.head.appendChild(linkLeaflet)
    document.head.appendChild(linkMapion)
  },
  async created() {
    await Promise.all([
      this.readStoreOcShufooAttributes(),
      this.readStore({ company_uuid: this.companyUuid, store_uuid: this.storeUuid }),
    ])
    this.$emit(
      'update-title',
      this.$t('pages.CompaniesLocationStoreShufooEdit.title', {
        name: this.store.attributes.name
      })
    )
    this.initData()
    if(!this.map) this.setMap()
    if(!this.marker) this.setMarker()
  },
  methods: {
    ...mapActions({
      readStoreOcShufooAttributes: 'companiesStoreOcShufooAttributes/readStoreOcShufooAttributes',
      updateStoreOcShufooAttributes: 'companiesStoreOcShufooAttributes/updateStoreOcShufooAttributes',
      readStore: 'companiesLocationStore/readStore',
    }),
    initData() {
      if(this.shufooAttributes.businessHoursCaption) this.form.businessHoursCaption = this.shufooAttributes.businessHoursCaption
      if(this.shufooAttributes.code) this.form.code = this.shufooAttributes.code
      if(this.shufooAttributes.referralUrl) this.form.referralUrl = this.shufooAttributes.referralUrl
      if(this.shufooAttributes.publishStatus) this.form.publishStatus = this.shufooAttributes.publishStatus
      if(this.shufooAttributes.latitude) this.form.latitude = this.shufooAttributes.latitude
      if(this.shufooAttributes.longitude) this.form.longitude = this.shufooAttributes.longitude
    },
    setMap() {
      this.map = window.L.map.mapion('map', {
        center: L.latLng(35.6809591, 139.7673068),
        maxBounds: [
          [boundaryOfJapanMap.northwest.latitude, boundaryOfJapanMap.northwest.longitude], // 左上
          [boundaryOfJapanMap.southeast.latitude, boundaryOfJapanMap.southeast.longitude] // 右下
        ],
        zoom: 18
      })
      this.map.invalidateSize()
    },
    setMarker() {
      this.marker = L.marker(L.latLng(35.6809591, 139.7673068), {draggable: true})
      this.marker.on('dragend', (event) => {
        const _marker = event.target
        const _point = _marker.getLatLng()

        this.form.latitude = _point.lat
        this.form.longitude = _point.lng
      })
      this.map.addLayer(this.marker)
    },
    async handleUpdateAttributes() {
      const payload = {
        storeOcShufooAttributes: {
          businessHoursCaption: this.form.businessHoursCaption,
          code: this.form.code,
          referralUrl: this.form.referralUrl,
          latitude: this.form.latitude,
          longitude: this.form.longitude,
          publishStatus: this.form.publishStatus
        }
      }
      const isSuccess = await this.updateStoreOcShufooAttributes(payload)
      if(isSuccess) this.$router.push({ name: 'CompaniesLocationStore', params: { company_uuid: this.companyUuid, store_uuid: this.storeUuid } })
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  padding: 24px 24px 50px;
  margin-bottom: 24px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  .c-formLabel,
  .c-checkbox {
    margin-top: 28px;
  }
}
.c-info:not(:last-child) {
  margin-bottom: 24px;
}
.site {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
  letter-spacing: 1px;
  &__name {
    margin-left: 8px;
  }
}
.select {
  width: 30%;
}

.example {
  font-size: 12px;
}

.geoContainer {
  display: flex;
  justify-content: space-around;
  &__form {
    width: 50%;
  }
  #map {
    position: relative;
    width: 40%;
    height: 400px;
  }
}
</style>
